import { ArrowUpDownIcon } from 'lucide-react'
import { Button, TableHead } from '@rentflowdev/ui'
import type { InvoiceSortBy, SortOrder } from '@/types'

interface SortableHeadProps {
	/** Column this header orders by. */
	field: InvoiceSortBy
	activeField?: InvoiceSortBy
	order: SortOrder
	onSort: (field: InvoiceSortBy) => void
	className?: string
	children: React.ReactNode
}

/**
 * A column header that actually sorts.
 *
 * The finance tables already drew the arrow next to each column, but nothing
 * was wired to it — the button had no handler, so the icon promised an action
 * that did not exist. The arrow now tints when its column is the active one,
 * which is the only way to tell what the list is ordered by.
 *
 * Ordering itself is a server parameter: both lists are paginated by the
 * server, and sorting in the browser would reorder one page.
 */
export function SortableHead({
	field,
	activeField,
	order,
	onSort,
	className,
	children,
}: SortableHeadProps) {
	const active = activeField === field

	return (
		<TableHead className={className}>
			<Button
				variant="ghost"
				onClick={() => onSort(field)}
				aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
				iconRight={
					<ArrowUpDownIcon
						className={`size-3.5${active ? ' text-(--color-foreground)' : ''}`}
					/>
				}
				className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
			>
				{children}
			</Button>
		</TableHead>
	)
}
